/**
 * Login Page
 * ==========
 * User authentication page with email/password login
 * Split-screen layout inspired by Jampack design
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { useAuth, useDocumentTitle } from '../hooks';
import { Button, Input, Alert } from '../components/ui';
import { useOrgBranding } from '../hooks/useOrgBranding';
import { DEFAULT_BRAND_NAME } from '../utils/constants';
import AuthLogo from '../components/auth/AuthLogo';
import BrandWordmark from '../components/auth/BrandWordmark';
import Captcha, { captchaEnabled } from '../components/Captcha';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug } = useParams();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const { branding: orgBranding, resolved: brandingResolved } = useOrgBranding(orgSlug);
  const brandName = orgBranding?.name || DEFAULT_BRAND_NAME;
  useDocumentTitle(`${brandName} Admin Portal`);

  // Stable callbacks so the Captcha effect doesn't re-render the widget on every keystroke.
  const handleCaptchaVerify = useCallback((token) => setCaptchaToken(token), []);
  const handleCaptchaExpire = useCallback(() => setCaptchaToken(null), []);
  const handleCaptchaLoadError = useCallback(() => setCaptchaUnavailable(true), []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }

    if (apiError) {
      setApiError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await login(formData.email, formData.password, orgSlug || null, captchaToken);

      // This is an AJAX/SPA login: handleSubmit calls preventDefault() and the
      // redirect happens via React state, not a browser navigation. Because the
      // browser never observes a native form submit + navigation, its password
      // manager won't offer to save the credentials on its own. The Credential
      // Management API explicitly triggers that "Save password?" prompt.
      // Only available in a secure context (HTTPS / localhost) on Chromium.
      if (typeof window !== 'undefined' && window.PasswordCredential) {
        try {
          const credential = new window.PasswordCredential({
            id: formData.email,
            password: formData.password,
            name: formData.email,
          });
          await navigator.credentials.store(credential);
        } catch (credError) {
          // Progressive enhancement — never block sign-in if storage fails.
          console.debug('Credential store skipped:', credError);
        }
      }
    } catch (error) {
      console.error('Login error:', error);

      if (error.code === 'VALIDATION_ERROR' && error.details) {
        setErrors(error.details);
      }

      setApiError({
        message:
          error.code === 'CAPTCHA_FAILED'
            ? 'CAPTCHA verification failed. Please complete the challenge and try again.'
            : error.message,
        code: error.code,
        details: error.details,
      });
    } finally {
      setLoading(false);
      // reCAPTCHA v2 tokens are single-use and are consumed before the password
      // check, so a wrong-password retry would resend a spent token. Clear and
      // remount the widget after every attempt.
      if (captchaEnabled) {
        setCaptchaToken(null);
        setCaptchaKey((k) => k + 1);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Show the org's own logo/name when it has branding; otherwise fall back to
  // the default zCortexAI product wordmark so the login always carries a logo.
  const hasOrgBranding = Boolean(orgBranding?.logo_url || orgBranding?.name);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12">
      {/* ===== Soft brand backdrop ===== */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,125,136,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(0,125,136,0.055)_1px,transparent_1px)] bg-[size:28px_28px]" />
        <div className="absolute -top-40 left-1/4 h-[32rem] w-[42rem] -translate-x-1/2 rounded-full bg-primary/20 blur-[130px]" />
        <div className="absolute -bottom-40 right-0 h-[28rem] w-[32rem] translate-x-1/4 rounded-full bg-accent/20 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[110px]" />
      </div>

      {/* ===== Elevated card ===== */}
      <div className="relative w-full max-w-md animate-slide-up">
        <div className="overflow-hidden rounded-2xl bg-white shadow-strong ring-1 ring-neutral-900/5">
          <div className="px-8 py-10 sm:px-10">
            {/* Logo / Org Branding */}
            <div className="mb-8 flex justify-center">
              {/* While branding is still loading (org-scoped login), defer to
                  AuthLogo's height-reserving placeholder so the default
                  wordmark never flashes before the org logo resolves. */}
              {!brandingResolved || hasOrgBranding ? (
                <AuthLogo branding={orgBranding} resolved={brandingResolved} variant="light" />
              ) : (
                <Link to="/" aria-label={DEFAULT_BRAND_NAME}>
                  <BrandWordmark tone="dark" size="lg" />
                </Link>
              )}
            </div>

            {/* Heading */}
            <h1 className="text-center text-2xl font-bold text-neutral-900">
              Sign in to your account
            </h1>
            <p className="mt-2 mb-8 text-center text-neutral-500">
              Welcome back to {brandName}. Enter your credentials to continue.
            </p>

            {/* API Error Alert */}
            {apiError && (
              <div className="mb-6">
                <Alert
                  variant={apiError.code === 'NETWORK_ERROR' ? 'info' : 'error'}
                  title={apiError.code === 'NETWORK_ERROR' ? 'Connection Issue' : 'Error'}
                >
                  {apiError.message}
                  {apiError.details?.attempts_remaining !== undefined && (
                    <p className="mt-1 text-sm">
                      Attempts remaining: {apiError.details.attempts_remaining}
                    </p>
                  )}
                  {apiError.details?.locked_until && (
                    <p className="mt-1 text-sm">
                      Locked until: {new Date(apiError.details.locked_until).toLocaleString()}
                    </p>
                  )}
                </Alert>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <Input
                id="email"
                name="email"
                type="email"
                label="Email Address"
                required
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="Enter your email"
              />

              {/* Password Field */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label label-required" htmlFor="password">Password</label>
                  <a
                    href={orgSlug ? `/${orgSlug}/forgot-password` : '/forgot-password'}
                    className="text-sm font-medium text-primary hover:text-primary-600"
                  >
                    Forgot Password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className={`input ${errors.password ? 'input-error' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-primary-600 hover:text-primary-700 uppercase tracking-wide"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* CAPTCHA */}
              {captchaEnabled && (
                <div>
                  <Captcha
                    key={captchaKey}
                    onVerify={handleCaptchaVerify}
                    onExpire={handleCaptchaExpire}
                    onLoadError={handleCaptchaLoadError}
                  />
                  {captchaUnavailable && (
                    <p className="mt-2 text-sm text-neutral-500 text-center">
                      CAPTCHA couldn't load. You can still sign in.
                    </p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  disabled={loading || (captchaEnabled && !captchaToken && !captchaUnavailable)}
                  loading={loading}
                  className="w-full"
                >
                  {loading ? 'Signing in...' : 'LOGIN'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-neutral-400">
          {brandName} &copy; {new Date().getFullYear()} All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
